import React from "react";
import { useApi } from "../hooks/useApi";

const card = { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "16px" };

function Bar({ pct, color }) {
  return (
    <div style={{ height: "6px", borderRadius: "3px", background: "#1e2a45", marginTop: "4px", overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: color, borderRadius: "3px", transition: "width 0.5s" }} />
    </div>
  );
}

// v5.16: clusters come from /intelligence.clusters, which is config.
// ⚠ The hardcoded v5.1 copy that lived here survived the v5.14 swap and went on
// displaying a USD_LONG cluster whose three members (USDJPY/USDCHF/USDCAD) had
// all been removed from the engine — a risk panel showing exposure groupings
// that no longer exist is worse than one showing none.

export default function RiskPanel() {
  const { data, loading } = useApi("/risk_status", 10000);
  const { data: intel }   = useApi("/intelligence", 60000);
  const CLUSTERS = intel?.clusters || {};

  if (loading) return (
    <div style={card}>
      <div style={{ color: "#475569", fontSize: "13px" }}>🛡️ Loading risk data...</div>
    </div>
  );

  const equity        = data?.equity || 0;
  const portfolioRisk = data?.portfolio_risk_usd || 0;
  const portfolioPct  = equity > 0 ? (portfolioRisk / equity * 100) : 0;
  const portfolioBar  = portfolioPct / 6 * 100; // 6% = 100% of bar

  // v5.1 Portfolio Heat (correlation-adjusted) — API returns object {raw_heat, adjusted_heat, status}
  const portfolioHeatObj  = data?.portfolio_heat;
  const portfolioHeatPct  = portfolioHeatObj?.adjusted_heat || 0;
  const heatStatus        = portfolioHeatObj?.status || "OK";
  const heatColor         = heatStatus === "HALT" ? "#ef4444" : heatStatus === "WARNING" ? "#f59e0b" : "#22c55e";

  // v5.1 Circuit Breaker Status — is_paused is dict of symbol→bool
  const circuitBreaker    = data?.circuit_breaker || {};
  const pausedSymbols     = circuitBreaker.is_paused || {};
  const anyPaused         = Object.values(pausedSymbols).some(v => v === true);
  const pauseReason       = anyPaused
    ? Object.entries(pausedSymbols).filter(([,v]) => v).map(([k]) => k).join(", ") + " paused"
    : "";
  const consecutiveLosses = circuitBreaker.consecutive_losses || {};
  const dailyLosses       = circuitBreaker.total_losses_today || 0;

  return (
    <div style={card}>
      <div style={{ fontWeight: 600, color: "#94a3b8", marginBottom: "12px" }}>🛡️ 1-2-4 Risk Monitor</div>

      {/* v5.1 Circuit Breaker Status */}
      {anyPaused && (
        <div style={{
          marginBottom: "12px", padding: "8px 10px",
          background: "#450a0a", borderRadius: "6px",
          border: "1px solid #7f1d1d",
        }}>
          <div style={{ fontSize: "11px", color: "#f87171", fontWeight: 600 }}>
            ⚠️ TRADING PAUSED
          </div>
          <div style={{ fontSize: "10px", color: "#fca5a5", marginTop: "2px" }}>
            {pauseReason.replace(/_/g, " ")}
          </div>
        </div>
      )}

      {/* v5.1 Portfolio Heat */}
      <div style={{ marginBottom: "12px", padding: "8px", background: "#0a0e1a", borderRadius: "6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px" }}>
          <span style={{ color: "#64748b" }}>Portfolio Heat (corr-adjusted)</span>
          <span style={{ color: heatColor, fontWeight: 600 }}>
            {portfolioHeatPct.toFixed(1)}% {heatStatus !== "OK" && `(${heatStatus})`}
          </span>
        </div>
        <Bar pct={portfolioHeatPct / 8 * 100} color={heatColor} />
        <div style={{ fontSize: "9px", color: "#334155", marginTop: "2px" }}>
          Warn @ 6% · Halt @ 8%
        </div>
      </div>

      {/* Portfolio Risk */}
      <div style={{ marginBottom: "14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
          <span style={{ color: "#64748b" }}>Portfolio Risk (raw)</span>
          <span style={{ color: portfolioBar > 75 ? "#ef4444" : "#60a5fa" }}>
            ${portfolioRisk.toFixed(2)} ({portfolioPct.toFixed(1)}% / 6%)
          </span>
        </div>
        <Bar pct={portfolioBar} color={portfolioBar > 75 ? "#ef4444" : "#60a5fa"} />
      </div>

      {(data?.by_symbol || []).length === 0 && (
        <div style={{ color: "#334155", fontSize: "12px" }}>No open risk</div>
      )}

      {(data?.by_symbol || []).map((s) => {
        const pct    = equity > 0 ? (s.risk_usd / equity * 100) : 0;
        const barPct = pct / 2 * 100; // 2% = 100% of bar
        const consecLoss = consecutiveLosses[s.symbol] || 0;
        return (
          <div key={s.symbol} style={{ marginBottom: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px" }}>
              <span style={{ color: "#94a3b8" }}>{s.symbol}</span>
              <span style={{ color: barPct > 75 ? "#f59e0b" : "#475569" }}>
                ${s.risk_usd.toFixed(2)} ({pct.toFixed(1)}%)
                {consecLoss >= 3 && <span style={{ color: "#ef4444", marginLeft: "4px" }}>
                  ({consecLoss} losses)
                </span>}
              </span>
            </div>
            <Bar pct={barPct} color={barPct > 75 ? "#f59e0b" : "#334155"} />
          </div>
        );
      })}

      {/* v5.1 Correlation Clusters */}
      <div style={{ marginTop: "12px", fontSize: "10px", color: "#475569", borderTop: "1px solid #1e2a45", paddingTop: "8px" }}>
        <div style={{ fontWeight: 600, marginBottom: "4px" }}>Correlation Clusters:</div>
        {Object.entries(CLUSTERS).map(([name, syms]) => {
          const activeSyms = (data?.by_symbol || []).filter(s => syms.includes(s.symbol));
          const isActive = activeSyms.length >= 2;
          return (
            <div key={name} style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
              <span style={{ color: isActive ? "#f59e0b" : "#334155" }}>{name}</span>
              <span style={{ color: isActive ? "#f59e0b" : "#334155" }}>
                {activeSyms.length}/{syms.length} active {isActive && "⚠️"}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: "10px", fontSize: "11px", color: "#334155", borderTop: "1px solid #1e2a45", paddingTop: "8px" }}>
        <div>True 1-2-4: 1% / tranche · 2% / asset · 4% portfolio · 2% surplus</div>
        <div style={{ color: dailyLosses >= 5 ? "#f59e0b" : "#334155" }}>
          Daily losses: {dailyLosses} / 10
        </div>
      </div>
    </div>
  );
}
